import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  Box,
  Typography,
  Modal,
  Fade,
  IconButton,
  Button,
  Stack,
  TextField,
  LinearProgress,
} from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PostAddRoundedIcon from '@mui/icons-material/PostAddRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

import api from '../../../../utils/functions/api';
import { confirmAsync, showAlert } from '../../../../utils/functions/globalDialog';
import resizeImageFileIfNeeded from '../../../../utils/functions/resizeImageFile';
import * as actions from '../../../../redux/actions';
import FileUpload from '../../../../components/FileUpload/FileUpload';
import RecurringExpenseFields from '../RecurringExpenseFields';
import { MODAL_STYLE, ACCENT_GRADIENT, ACCENT_COLOR } from '../constants';
import { applyNameVars, formatPeriodLabel, formatAmountVar, editNameSegments } from '../utils';

// ฟอร์มสร้างรายการเบิกจริงของงวดหนึ่งจาก template — เตรียมค่าให้แล้ว: วันที่รับเงิน/โครงการ/งบประมาณ/
// รายการ (แทนตัวแปรเดือน-ปีของงวดแล้ว)/ประเภทการเบิก/ผู้รับเงิน ที่เหลือ (ยอด/VAT/หัก ณ ที่จ่าย/หลักฐาน)
// กรอกเองทุกงวด — {ยอด} ในชื่อรายการเปลี่ยนเป็นยอดจ่ายสุทธิในช่องทันทีที่กรอกยอด (ดู nameSegments) ยิงไปที่
// POST /expenses-recurring/:id/create-expense (backend กันสร้างซ้ำงวดเดียวกัน + ผูก template/งวดให้เอง)
// target = { row: template, period: { period, due_date, status } } หรือ null = ปิด
export default function CreateExpenseFromRecurringModal({ target, onClose, onCreated }) {
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const projects = useSelector((state) => state.project);
  const budgetState = useSelector((state) => state.budget);
  const customer = useSelector((state) => state.customer);

  const open = !!target;
  const row = target?.row;
  const periodInfo = target?.period;

  const [date, setDate] = useState(new Date());
  const [projectSelect, setProjectSelect] = useState(null);
  const [budgetSelect, setBudgetSelect] = useState(null);
  const [customerSelect, setCustomerSelect] = useState(null);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // ช่อง "รายการ" แบ่งเป็นท่อนตามตำแหน่ง {ยอด} — โชว์เป็น nameSegments.join(ยอดจ่ายสุทธิ) ให้ตัวเลขเปลี่ยนตามยอด
  // ทันที (ดู editNameSegments ใน ../utils.js)
  const [nameSegments, setNameSegments] = useState(['']);

  const { control, handleSubmit, reset, setValue, watch } = useForm();

  useEffect(() => {
    if (!open) return;
    dispatch(actions.projectAll({ lite: true }));
    dispatch(actions.customerAll({}));
    if (!me?.userData?._id) dispatch(actions.meGet());
  }, [dispatch, open]);

  useEffect(() => {
    if (projectSelect?._id) {
      dispatch(actions.budgetAll({ project_id: projectSelect._id }));
    }
  }, [dispatch, projectSelect]);

  // เติมค่าตั้งต้นจาก template ทุกครั้งที่เปิดสำหรับงวดใหม่
  useEffect(() => {
    if (!open) return;
    reset({});
    setFiles([]);
    setUploadProgress(0);
    setDate(new Date(periodInfo.due_date));
    setProjectSelect(row.project || null);
    setBudgetSelect(row.budget || null);
    setCustomerSelect(row.customer || null);
    setValue('project', row.project?._id);
    setValue('budget', row.budget?._id);
    setValue('expenses_type', row.expenses_type || 'REFUND');
    setNameSegments(applyNameVars(row.name, periodInfo.due_date).split('{ยอด}'));
    setValue('customer', row.customer?._id);
    setValue('payee.bank', row.payee?.bank || row.customer?.bank?.bank_name || '');
    setValue('payee.account_number', row.payee?.account_number || row.customer?.bank?.account_number || '');
    setValue('payee.name', row.payee?.name || row.customer?.bank?.account_name || '');
  }, [open, row?._id, periodInfo?.period]);

  const netPrice = Number(watch('net_price')) || 0;
  // ยังไม่กรอกยอด = คง {ยอด} ไว้ให้เห็นว่าตรงนี้จะเติมยอดให้
  const amountText = netPrice > 0 ? formatAmountVar(netPrice) : '{ยอด}';
  const renderedName = nameSegments.join(amountText);
  useEffect(() => {
    if (open) setValue('name', renderedName);
  }, [open, renderedName, setValue]);
  const nameHelper = nameSegments.length > 1 ? (
    <Box component="span" sx={{ display: 'block', color: ACCENT_COLOR }}>
      ยอดในรายการเปลี่ยนตามยอดจ่ายจริงสุทธิอัตโนมัติ (ถ้าแก้ทับตัวเลขยอดเอง จะเลิกเปลี่ยนตาม)
    </Box>
  ) : null;

  const onSubmit = async (data) => {
    if (!projectSelect) {
      showAlert('กรุณาเลือกโครงการ');
      return;
    }
    if (!budgetSelect) {
      showAlert('กรุณาเลือกงบประมาณ');
      return;
    }
    if (files.length === 0) {
      showAlert('กรุณาแนบหลักฐานอย่างน้อย 1 ไฟล์');
      return;
    }
    const confirm = await confirmAsync('บันทึกข้อมูล');
    if (!confirm) return;

    setSubmitting(true);
    try {
      const toUpload = await Promise.all(
        files.map(async (f) => ({ file: await resizeImageFileIfNeeded(f.file), name: f.name })),
      );
      let done = 0;
      const billImages = await Promise.all(
        toUpload.map(async ({ file, name }) => {
          const formData = new FormData();
          formData.append('file', file, name);
          const { data: uploaded } = await api.post(
            `${process.env.REACT_APP_API_URL}/expenses/upload-image`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          done += 1;
          setUploadProgress(Math.round((done / toUpload.length) * 100));
          return uploaded.id;
        }),
      );

      const userData = me?.userData;
      await api.post(`${process.env.REACT_APP_API_URL}/expenses-recurring/${row._id}/create-expense`, {
        ...data,
        period: periodInfo.period,
        name: nameSegments.join(formatAmountVar(data.net_price || 0)),
        paidType: 'TRANSFER',
        date: dayjs(date).endOf('day').toISOString(),
        employee: userData?._id,
        employee_data: userData
          ? {
            title: userData.title,
            firstname: userData.firstname,
            lastname: userData.lastname,
            firstname_th: userData.firstname_th,
            lastname_th: userData.lastname_th,
          }
          : undefined,
        project: projectSelect?._id || undefined,
        budget: budgetSelect?._id || undefined,
        customer: customerSelect?._id || undefined,
        bill_images: billImages.filter(Boolean),
      });
      onCreated();
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={submitting ? undefined : onClose} closeAfterTransition>
      <Fade in={open}>
        <Box sx={MODAL_STYLE}>
          <Box
            sx={{
              background: ACCENT_GRADIENT,
              color: '#fff',
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PostAddRoundedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  สร้างรายการเบิก งวด {row ? formatPeriodLabel(row.recur_type, periodInfo?.period) : ''}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  จากรายการเบิกประจำ — กรอกยอดและแนบหลักฐานของงวดนี้
                </Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={onClose} disabled={submitting} sx={{ color: '#fff' }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          {open && (
            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 2.5, overflow: 'auto' }}>
              <Box sx={{ mb: 2, maxWidth: 240 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="วันที่รับเงิน"
                    inputFormat="dd/MM/yyyy"
                    mask="__/__/____"
                    value={date}
                    onChange={(d) => setDate(d)}
                    renderInput={(params) => <TextField size="small" fullWidth {...params} />}
                  />
                </LocalizationProvider>
              </Box>

              <RecurringExpenseFields
                expenses={null}
                control={control}
                Controller={Controller}
                setValue={setValue}
                watch={watch}
                projects={projects}
                projectSelect={projectSelect}
                setProjectSelect={setProjectSelect}
                budgets={budgetState}
                budgetSelect={budgetSelect}
                setBudgetSelect={setBudgetSelect}
                customer={customer}
                customerSelect={customerSelect}
                setCustomerSelect={setCustomerSelect}
                showAmount
                nameHelper={nameHelper}
                nameControl={{
                  value: renderedName,
                  onChange: (text) => setNameSegments((prev) => editNameSegments(prev, amountText, text)),
                }}
              />

              <Box sx={{ mt: 2.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  หลักฐาน <Typography component="span" color="error">*</Typography>
                </Typography>
                <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'action.hover', textAlign: 'center' }}>
                  <FileUpload
                    maxNumber={10}
                    files={files}
                    setFiles={setFiles}
                    title="เลือกรูปบิล / PDF"
                    accept="image/*,.pdf"
                  />
                </Box>
              </Box>

              {submitting && files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">กำลังอัปโหลดหลักฐาน {uploadProgress}%</Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              )}

              <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button variant="outlined" onClick={onClose} disabled={submitting}>ยกเลิก</Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={submitting}
                  startIcon={<SaveRoundedIcon />}
                  sx={{ background: ACCENT_GRADIENT, '&:hover': { background: ACCENT_GRADIENT, filter: 'brightness(0.95)' } }}
                >
                  สร้างรายการเบิก
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
}
